import api from './client';

export type FleetInstanceStatus =
  | 'pending'
  | 'provisioning'
  | 'live'
  | 'suspended'
  | 'decommissioned'
  | 'failed';

export type ProvisionJobStatus =
  | 'queued'
  | 'running'
  | 'awaiting_manual_review'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

/** Same order as API PROVISION_STAGE_ORDER (used for progress UI). */
export const PROVISION_STAGES = [
  'reserve',
  'database',
  'secrets',
  'api_host',
  'migrate_seed',
  'frontend',
  'dns',
  'register',
  'backup_enroll',
  'handoff',
  'done',
] as const;

export type ProvisionStage = (typeof PROVISION_STAGES)[number];

export const PROVISION_STAGE_LABELS: Record<string, string> = {
  reserve: 'Reserve instance ID',
  database: 'Create database',
  secrets: 'Prepare secrets',
  api_host: 'Create API host',
  migrate_seed: 'Migrate / seed',
  frontend: 'Frontend deploy note',
  dns: 'DNS note',
  register: 'Register fleet live',
  backup_enroll: 'Backup enroll',
  handoff: 'Email handoff',
  done: 'Done',
};

export interface FleetInstance {
  id: string;
  instanceId: string;
  subdomain: string;
  orgName: string;
  adminEmail: string;
  plan: string;
  status: FleetInstanceStatus;
  appUrl?: string | null;
  apiUrl?: string | null;
  signupIntentId?: string | null;
  neonProjectId?: string | null;
  flyAppName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProvisionJob {
  id: string;
  fleetInstanceId: string;
  signupIntentId?: string | null;
  status: ProvisionJobStatus;
  currentStage: string;
  attempts: number;
  lastError?: string | null;
  logs: Array<{ at: string; stage: string; message: string; level?: string }>;
  context: Record<string, unknown>;
  startedAt?: string | null;
  finishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProvisionTickResult {
  ok: boolean;
  processed: boolean;
  reason?: string;
  jobId?: string;
  subdomain?: string;
  statusBefore?: string;
  statusAfter?: string;
  stageBefore?: string;
  stageAfter?: string;
  stagesAdvanced?: number;
  lastError?: string | null;
}

export interface ProvisionReadiness {
  controlPlane?: boolean;
  mode: 'auto' | 'simulate' | 'manual' | 'strict';
  readyForStrict: boolean;
  detail?: string;
  checks: Array<{
    id: string;
    ok: boolean;
    requiredForStrict: boolean;
    detail: string;
  }>;
}

export const fleetApi = {
  listInstances: async () => {
    const { data } = await api.get<FleetInstance[]>('fleet/admin/instances');
    return data;
  },
  listJobs: async (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    const { data } = await api.get<ProvisionJob[]>(`fleet/admin/jobs${q}`);
    return data;
  },
  getJob: async (id: string) => {
    const { data } = await api.get<{ job: ProvisionJob; fleet: FleetInstance | null }>(
      `fleet/admin/jobs/${id}`,
    );
    return data;
  },
  retryJob: async (id: string) => {
    const { data } = await api.post<{ job: ProvisionJob; fleet: FleetInstance | null }>(
      `fleet/admin/jobs/${id}/retry`,
    );
    return data;
  },
  tickWorker: async () => {
    const { data } = await api.post<ProvisionTickResult>('fleet/admin/worker/tick');
    return data;
  },
  getProvisionReadiness: async () => {
    const { data } = await api.get<ProvisionReadiness>('fleet/admin/provision-readiness');
    return data;
  },
  decommissionInstance: async (id: string, reason?: string) => {
    const { data } = await api.post<{ instance: FleetInstance; steps: string[] }>(
      `fleet/admin/instances/${encodeURIComponent(id)}/decommission`,
      { reason: reason || 'ops_request' },
    );
    return data;
  },
  suspendInstance: async (id: string) => {
    const { data } = await api.post<FleetInstance>(
      `fleet/admin/instances/${encodeURIComponent(id)}/suspend`,
    );
    return data;
  },
};
